import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { getCompanySettings, updateCompanySettings, syncAllData, getLwfStates, getSmtpSettings, saveSmtpSettings, testSmtpConnection } from '../api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from '../components/ui/dialog';
import { Building2, MapPin, Mail, Phone, Globe, CreditCard, Save, RefreshCw, Database, CheckCircle2, Shield, Plus, Trash2, Users, IndianRupee, Send, Eye, EyeOff, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

// LWF applicable states with rates
const LWF_STATES_CONFIG = {
  "Maharashtra": { employee: 12, employer: 36, frequency: "Biannual (Jun, Dec)" },
  "Karnataka": { employee: 20, employer: 40, frequency: "Annual (Jan)" },
  "Tamil Nadu": { employee: 10, employer: 20, frequency: "Annual (Feb)" },
  "Gujarat": { employee: 6, employer: 12, frequency: "Biannual (Jun, Dec)" },
  "Madhya Pradesh": { employee: 10, employer: 30, frequency: "Biannual (Jun, Dec)" },
  "West Bengal": { employee: 3, employer: 5, frequency: "Biannual (Jun, Dec)" },
  "Andhra Pradesh": { employee: 30, employer: 60, frequency: "Annual (Jan)" },
  "Telangana": { employee: 2, employer: 5, frequency: "Annual (Jan)" },
  "Kerala": { employee: 20, employer: 20, frequency: "Biannual (Jan, Jul)" },
  "Odisha": { employee: 20, employer: 40, frequency: "Biannual (Jun, Dec)" },
  "Chhattisgarh": { employee: 15, employer: 40, frequency: "Biannual (Jun, Dec)" },
  "Jharkhand": { employee: 20, employer: 40, frequency: "Annual (Dec)" },
  "Goa": { employee: 60, employer: 180, frequency: "Annual (Jan)" },
  "Punjab": { employee: 5, employer: 20, frequency: "Biannual (Jun, Dec)" },
  "Haryana": { employee: 10, employer: 25, frequency: "Monthly" },
  "Delhi": { employee: 1, employer: 2, frequency: "Biannual (Jun, Dec)" },
  "Chandigarh": { employee: 5, employer: 20, frequency: "Biannual (Jun, Dec)" },
};

const CompanySettings = ({ user, setUser }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [addLwfDialogOpen, setAddLwfDialogOpen] = useState(false);
  const [newLwfState, setNewLwfState] = useState('');
  const [newLwfRegNumber, setNewLwfRegNumber] = useState('');
  const [newLwfRegDate, setNewLwfRegDate] = useState('');
  // SMTP Settings state
  const [smtpData, setSmtpData] = useState({
    smtp_host: 'smtp.gmail.com',
    smtp_port: 587,
    smtp_email: '',
    smtp_password: '',
    smtp_from_name: 'T&N Business Services',
  });
  const [smtpMeta, setSmtpMeta] = useState({ source: '', configured: false, password_masked: '' });
  const [smtpSaving, setSmtpSaving] = useState(false);
  const [smtpTesting, setSmtpTesting] = useState(false);
  const [smtpTestResult, setSmtpTestResult] = useState(null);
  const [showSmtpPassword, setShowSmtpPassword] = useState(false);
  const [smtpPasswordEdited, setSmtpPasswordEdited] = useState(false);
  const [formData, setFormData] = useState({
    company_name: '',
    company_legal_name: '',
    logo_url: '',
    address: '',
    city: '',
    state: '',
    pincode: '',
    country: 'India',
    email: '',
    phone: '',
    website: '',
    portal_base_url: '',
    pan: '',
    tan: '',
    gstin: '',
    // Statutory Registrations
    pf_registration_number: '',
    pf_registration_date: '',
    esic_registration_number: '',
    esic_registration_date: '',
    pt_registration_number: '',
    pt_registration_state: '',
    shops_establishment_number: '',
    shops_establishment_state: '',
    // LWF - Multiple States
    lwf_registrations: [],
    // Notification Emails
    hr_email: '',
    finance_email: '',
    // Bank
    bank_name: '',
    bank_account: '',
    ifsc_code: ''
  });

  useEffect(() => {
    fetchSettings();
    fetchSmtpSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await getCompanySettings();
      // Ensure lwf_registrations is always an array
      const data = {
        ...response.data,
        lwf_registrations: response.data.lwf_registrations || []
      };
      setFormData(data);
    } catch (error) {
      if (error.response?.status === 404) {
        toast.info('Please configure company settings');
      } else {
        toast.error('Failed to fetch company settings');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await updateCompanySettings(formData);
      toast.success('Company settings updated successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  const fetchSmtpSettings = async () => {
    try {
      const res = await getSmtpSettings();
      const d = res.data;
      setSmtpData({
        smtp_host: d.smtp_host || 'smtp.gmail.com',
        smtp_port: d.smtp_port || 587,
        smtp_email: d.smtp_email || '',
        smtp_password: '',
        smtp_from_name: d.smtp_from_name || 'T&N Business Services',
      });
      setSmtpMeta({ source: d.source, configured: d.configured, password_masked: d.smtp_password_masked || '' });
      setSmtpPasswordEdited(false);
    } catch {
      // SMTP settings not yet configured — leave defaults
    }
  };

  const handleSmtpSave = async () => {
    if (!smtpData.smtp_email) {
      toast.error('SMTP Email is required');
      return;
    }
    if (!smtpPasswordEdited && !smtpData.smtp_password) {
      toast.error('Please enter the SMTP password');
      return;
    }
    setSmtpSaving(true);
    try {
      const res = await saveSmtpSettings(smtpData);
      toast.success(res.data.message || 'SMTP settings saved');
      setSmtpMeta(prev => ({ ...prev, source: 'database', configured: true, password_masked: res.data.smtp_password_masked }));
      setSmtpData(prev => ({ ...prev, smtp_password: '' }));
      setSmtpPasswordEdited(false);
      setSmtpTestResult(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save SMTP settings');
    } finally {
      setSmtpSaving(false);
    }
  };

  const handleSmtpTest = async () => {
    setSmtpTesting(true);
    setSmtpTestResult(null);
    try {
      const res = await testSmtpConnection();
      setSmtpTestResult(res.data);
      if (res.data.status === 'SUCCESS') {
        toast.success('SMTP connection successful!');
      } else {
        toast.error(res.data.error || 'SMTP test failed');
      }
    } catch (error) {
      toast.error('Failed to test SMTP connection');
      setSmtpTestResult({ status: 'ERROR', error: error.response?.data?.detail || 'Test failed' });
    } finally {
      setSmtpTesting(false);
    }
  };

  const handleSmtpChange = (field, value) => {
    setSmtpData(prev => ({ ...prev, [field]: value }));
    if (field === 'smtp_password') setSmtpPasswordEdited(true);
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleAddLwfRegistration = () => {
    if (!newLwfState) {
      toast.error('Please select a state');
      return;
    }
    
    // Check if state already exists
    if (formData.lwf_registrations.some(r => r.state === newLwfState)) {
      toast.error('This state is already registered');
      return;
    }
    
    const newRegistration = {
      state: newLwfState,
      registration_number: newLwfRegNumber,
      registration_date: newLwfRegDate,
      ...LWF_STATES_CONFIG[newLwfState]
    };
    
    setFormData(prev => ({
      ...prev,
      lwf_registrations: [...prev.lwf_registrations, newRegistration]
    }));
    
    // Reset form
    setNewLwfState('');
    setNewLwfRegNumber('');
    setNewLwfRegDate('');
    setAddLwfDialogOpen(false);
    toast.success(`LWF registration for ${newLwfState} added`);
  };

  const handleRemoveLwfRegistration = (stateToRemove) => {
    setFormData(prev => ({
      ...prev,
      lwf_registrations: prev.lwf_registrations.filter(r => r.state !== stateToRemove)
    }));
    toast.success(`LWF registration for ${stateToRemove} removed`);
  };

  // Get states that are not yet registered
  const availableLwfStates = Object.keys(LWF_STATES_CONFIG).filter(
    state => !formData.lwf_registrations.some(r => r.state === state)
  );

  const handleSyncData = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const response = await syncAllData();
      setSyncResult(response.data);
      toast.success('Production data synchronized successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sync data');
      setSyncResult({ error: error.response?.data?.detail || 'Sync failed' });
    } finally {
      setSyncing(false);
    }
  };

  if (loading) {
    return (
      <Layout user={user} setUser={setUser}>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} setUser={setUser}>
      <div className="space-y-6" data-testid="company-settings-page">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" data-testid="page-title">Company Settings</h1>
          <p className="text-slate-600 mt-1">Manage your organization information</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <Card className="border border-slate-100 shadow-sm bg-white">
            <CardHeader className="bg-white border-b border-slate-100">
              <CardTitle className="flex items-center gap-2">
                <Building2 className="text-indigo-600" />
                Basic Information
              </CardTitle>
              <CardDescription>Company name and legal details</CardDescription>
            </CardHeader>
            <CardContent className="p-6 bg-white">
              <div className="grid grid-cols-1 gap-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="company_name">Company Name *</Label>
                    <Input
                      id="company_name"
                      value={formData.company_name}
                      onChange={(e) => handleChange('company_name', e.target.value)}
                      required
                      data-testid="company-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="company_legal_name">Legal Name *</Label>
                    <Input
                      id="company_legal_name"
                      value={formData.company_legal_name}
                      onChange={(e) => handleChange('company_legal_name', e.target.value)}
                      required
                      data-testid="legal-name-input"
                    />
                  </div>
                </div>
                
                {/* Logo Section */}
                <div className="space-y-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
                  <div className="space-y-2">
                    <Label htmlFor="logo_url">Company Logo URL</Label>
                    <Input
                      id="logo_url"
                      type="url"
                      value={formData.logo_url}
                      onChange={(e) => handleChange('logo_url', e.target.value)}
                      placeholder="https://example.com/logo.png"
                      data-testid="logo-url-input"
                    />
                    <p className="text-xs text-slate-500">
                      Enter a URL to your company logo. Recommended size: 200x200px (PNG, JPG, or SVG)
                    </p>
                  </div>
                  
                  {/* Logo Preview */}
                  {formData.logo_url && (
                    <div className="space-y-2">
                      <Label>Logo Preview</Label>
                      <div className="flex items-center gap-4 p-4 bg-white rounded-lg border border-slate-200">
                        <img
                          src={formData.logo_url}
                          alt="Company Logo"
                          className="h-16 w-16 object-contain"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'block';
                          }}
                        />
                        <div style={{display: 'none'}} className="text-sm text-red-600">
                          Failed to load logo. Please check the URL.
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-medium text-slate-900">{formData.company_name}</p>
                          <p className="text-xs text-slate-500">Logo will appear on login page, sidebar, and payslips</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Address Information */}
          <Card className="border border-slate-100 shadow-sm bg-white">
            <CardHeader className="bg-white border-b border-slate-100">
              <CardTitle className="flex items-center gap-2">
                <MapPin className="text-indigo-600" />
                Address Information
              </CardTitle>
              <CardDescription>Physical address and location</CardDescription>
            </CardHeader>
            <CardContent className="p-6 bg-white">
              <div className="grid grid-cols-1 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="address">Street Address *</Label>
                  <Input
                    id="address"
                    value={formData.address}
                    onChange={(e) => handleChange('address', e.target.value)}
                    required
                    data-testid="address-input"
                  />
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="city">City *</Label>
                    <Input
                      id="city"
                      value={formData.city}
                      onChange={(e) => handleChange('city', e.target.value)}
                      required
                      data-testid="city-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="state">State *</Label>
                    <Input
                      id="state"
                      value={formData.state}
                      onChange={(e) => handleChange('state', e.target.value)}
                      required
                      data-testid="state-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="pincode">Pincode *</Label>
                    <Input
                      id="pincode"
                      value={formData.pincode}
                      onChange={(e) => handleChange('pincode', e.target.value)}
                      required
                      data-testid="pincode-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="country">Country *</Label>
                    <Input
                      id="country"
                      value={formData.country}
                      onChange={(e) => handleChange('country', e.target.value)}
                      required
                      data-testid="country-input"
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Contact Information */}
          <Card className="border border-slate-100 shadow-sm bg-white">
            <CardHeader className="bg-white border-b border-slate-100">
              <CardTitle className="flex items-center gap-2">
                <Phone className="text-indigo-600" />
                Contact Information
              </CardTitle>
              <CardDescription>Communication details</CardDescription>
            </CardHeader>
            <CardContent className="p-6 bg-white">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    required
                    data-testid="email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone *</Label>
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => handleChange('phone', e.target.value)}
                    required
                    data-testid="phone-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="website">Website</Label>
                  <Input
                    id="website"
                    value={formData.website}
                    onChange={(e) => handleChange('website', e.target.value)}
                    placeholder="https://www.example.com"
                    data-testid="website-input"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 gap-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="portal_base_url">Live Portal URL</Label>
                  <Input
                    id="portal_base_url"
                    value={formData.portal_base_url || ''}
                    onChange={(e) => handleChange('portal_base_url', e.target.value)}
                    placeholder="https://your-live-portal.com"
                    data-testid="portal-base-url-input"
                  />
                  <p className="text-xs text-slate-500">Used for onboarding email links. Leave empty to use current domain.</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Tax Information */}
          <Card className="border border-slate-100 shadow-sm bg-white">
            <CardHeader className="bg-white border-b border-slate-100">
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="text-indigo-600" />
                Tax & Statutory Information
              </CardTitle>
              <CardDescription>PAN, TAN, GSTIN details</CardDescription>
            </CardHeader>
            <CardContent className="p-6 bg-white">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="pan">PAN *</Label>
                  <Input
                    id="pan"
                    value={formData.pan}
                    onChange={(e) => handleChange('pan', e.target.value)}
                    required
                    placeholder="AABTN1234F"
                    maxLength={10}
                    data-testid="pan-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="tan">TAN *</Label>
                  <Input
                    id="tan"
                    value={formData.tan}
                    onChange={(e) => handleChange('tan', e.target.value)}
                    required
                    placeholder="MUMTN2345A"
                    maxLength={10}
                    data-testid="tan-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="gstin">GSTIN *</Label>
                  <Input
                    id="gstin"
                    value={formData.gstin}
                    onChange={(e) => handleChange('gstin', e.target.value)}
                    required
                    placeholder="27AABTN1234F1Z5"
                    maxLength={15}
                    data-testid="gstin-input"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Statutory Registrations */}
          <Card className="border border-blue-100 shadow-sm bg-white">
            <CardHeader className="bg-blue-50 border-b border-blue-100">
              <CardTitle className="flex items-center gap-2 text-blue-800">
                <Shield className="text-blue-600" size={20} />
                Statutory Registrations
              </CardTitle>
              <CardDescription>PF, ESIC, PT, Shops & Est., LWF registration details</CardDescription>
            </CardHeader>
            <CardContent className="p-6 bg-white">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* PF Registration */}
                <div className="p-4 border border-slate-200 rounded-lg">
                  <h4 className="font-medium text-slate-700 mb-3">Provident Fund (PF)</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="pf_registration_number">PF Registration No.</Label>
                      <Input
                        id="pf_registration_number"
                        value={formData.pf_registration_number || ''}
                        onChange={(e) => handleChange('pf_registration_number', e.target.value)}
                        placeholder="MH/MUM/12345"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="pf_registration_date">Registration Date</Label>
                      <Input
                        id="pf_registration_date"
                        type="date"
                        value={formData.pf_registration_date || ''}
                        onChange={(e) => handleChange('pf_registration_date', e.target.value)}
                      />
                    </div>
                  </div>
                </div>
                {/* ESIC Registration */}
                <div className="p-4 border border-slate-200 rounded-lg">
                  <h4 className="font-medium text-slate-700 mb-3">ESIC</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="esic_registration_number">ESIC Registration No.</Label>
                      <Input
                        id="esic_registration_number"
                        value={formData.esic_registration_number || ''}
                        onChange={(e) => handleChange('esic_registration_number', e.target.value)}
                        placeholder="12-34-567890-000-0001"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="esic_registration_date">Registration Date</Label>
                      <Input
                        id="esic_registration_date"
                        type="date"
                        value={formData.esic_registration_date || ''}
                        onChange={(e) => handleChange('esic_registration_date', e.target.value)}
                      />
                    </div>
                  </div>
                </div>
                {/* Professional Tax */}
                <div className="p-4 border border-slate-200 rounded-lg">
                  <h4 className="font-medium text-slate-700 mb-3">Professional Tax (PT)</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="pt_registration_number">PT Registration No.</Label>
                      <Input
                        id="pt_registration_number"
                        value={formData.pt_registration_number || ''}
                        onChange={(e) => handleChange('pt_registration_number', e.target.value)}
                        placeholder="PT/MH/12345678"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="pt_registration_state">State</Label>
                      <Input
                        id="pt_registration_state"
                        value={formData.pt_registration_state || formData.state || ''}
                        onChange={(e) => handleChange('pt_registration_state', e.target.value)}
                        placeholder="Maharashtra"
                      />
                    </div>
                  </div>
                </div>
                {/* Shops & Establishment */}
                <div className="p-4 border border-slate-200 rounded-lg">
                  <h4 className="font-medium text-slate-700 mb-3">Shops & Establishment</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="shops_establishment_number">Registration No.</Label>
                      <Input
                        id="shops_establishment_number"
                        value={formData.shops_establishment_number || ''}
                        onChange={(e) => handleChange('shops_establishment_number', e.target.value)}
                        placeholder="SE/MUM/2024/12345"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="shops_establishment_state">State</Label>
                      <Input
                        id="shops_establishment_state"
                        value={formData.shops_establishment_state || formData.state || ''}
                        onChange={(e) => handleChange('shops_establishment_state', e.target.value)}
                        placeholder="Maharashtra"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Labour Welfare Fund - Multiple States */}
          <Card className="border border-emerald-200 shadow-sm bg-white">
            <CardHeader className="bg-emerald-50 border-b border-emerald-100">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2 text-emerald-800">
                    <Users className="text-emerald-600" size={20} />
                    Labour Welfare Fund (LWF) - Multi-State
                  </CardTitle>
                  <CardDescription>Register LWF for multiple states where your company operates</CardDescription>
                </div>
                <Dialog open={addLwfDialogOpen} onOpenChange={setAddLwfDialogOpen}>
                  <DialogTrigger asChild>
                    <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700">
                      <Plus className="mr-1" size={16} />
                      Add State
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle className="flex items-center gap-2">
                        <Users className="text-emerald-600" size={20} />
                        Add LWF Registration
                      </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="space-y-2">
                        <Label>Select State *</Label>
                        <Select value={newLwfState} onValueChange={setNewLwfState}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select a state" />
                          </SelectTrigger>
                          <SelectContent>
                            {availableLwfStates.map(state => (
                              <SelectItem key={state} value={state}>
                                <div className="flex justify-between items-center w-full">
                                  <span>{state}</span>
                                  <span className="text-xs text-slate-400 ml-2">
                                    ₹{LWF_STATES_CONFIG[state].employee}+₹{LWF_STATES_CONFIG[state].employer}
                                  </span>
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      {newLwfState && (
                        <div className="p-3 bg-emerald-50 rounded-lg text-sm">
                          <p className="font-medium text-emerald-800">{newLwfState} LWF Rates:</p>
                          <div className="grid grid-cols-2 gap-2 mt-2">
                            <p className="text-emerald-700">Employee: ₹{LWF_STATES_CONFIG[newLwfState].employee}</p>
                            <p className="text-emerald-700">Employer: ₹{LWF_STATES_CONFIG[newLwfState].employer}</p>
                          </div>
                          <p className="text-emerald-600 mt-1">Frequency: {LWF_STATES_CONFIG[newLwfState].frequency}</p>
                        </div>
                      )}
                      
                      <div className="space-y-2">
                        <Label htmlFor="new_lwf_reg_number">Registration Number</Label>
                        <Input
                          id="new_lwf_reg_number"
                          value={newLwfRegNumber}
                          onChange={(e) => setNewLwfRegNumber(e.target.value)}
                          placeholder="LWF/MH/12345"
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="new_lwf_reg_date">Registration Date</Label>
                        <Input
                          id="new_lwf_reg_date"
                          type="date"
                          value={newLwfRegDate}
                          onChange={(e) => setNewLwfRegDate(e.target.value)}
                        />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setAddLwfDialogOpen(false)}>Cancel</Button>
                      <Button 
                        onClick={handleAddLwfRegistration}
                        disabled={!newLwfState}
                        className="bg-emerald-600 hover:bg-emerald-700"
                      >
                        Add Registration
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent className="p-6 bg-white">
              {formData.lwf_registrations.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Users size={48} className="mx-auto mb-3 text-slate-300" />
                  <p>No LWF registrations added yet</p>
                  <p className="text-sm mt-1">Click "Add State" to register LWF for applicable states</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* LWF States Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {formData.lwf_registrations.map((reg, index) => {
                      const stateConfig = LWF_STATES_CONFIG[reg.state] || {};
                      return (
                        <div key={index} className="p-4 border border-emerald-200 rounded-lg bg-emerald-50/50 relative">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="absolute top-2 right-2 text-red-500 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0"
                            onClick={() => handleRemoveLwfRegistration(reg.state)}
                          >
                            <Trash2 size={16} />
                          </Button>
                          
                          <h4 className="font-semibold text-emerald-800 pr-8">{reg.state}</h4>
                          
                          <div className="mt-2 space-y-1 text-sm">
                            <p className="text-slate-600">
                              <span className="font-medium">Reg No:</span> {reg.registration_number || 'Not entered'}
                            </p>
                            {reg.registration_date && (
                              <p className="text-slate-600">
                                <span className="font-medium">Date:</span> {reg.registration_date}
                              </p>
                            )}
                          </div>
                          
                          <div className="mt-3 pt-3 border-t border-emerald-200">
                            <div className="flex justify-between text-xs">
                              <span className="text-emerald-700">
                                <IndianRupee size={10} className="inline" />
                                {stateConfig.employee || reg.employee} (Emp)
                              </span>
                              <span className="text-emerald-700">
                                <IndianRupee size={10} className="inline" />
                                {stateConfig.employer || reg.employer} (Empr)
                              </span>
                            </div>
                            <p className="text-xs text-emerald-600 mt-1">
                              {stateConfig.frequency || reg.frequency}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  
                  {/* Summary */}
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <h4 className="font-medium text-slate-700 mb-2">LWF Summary</h4>
                    <div className="flex flex-wrap gap-4 text-sm">
                      <span className="px-3 py-1 bg-emerald-100 text-emerald-800 rounded-full">
                        {formData.lwf_registrations.length} State(s) Registered
                      </span>
                      <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full">
                        {Object.keys(LWF_STATES_CONFIG).length - availableLwfStates.length} of {Object.keys(LWF_STATES_CONFIG).length} Applicable States
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Notification Emails */}
          <Card className="border border-slate-100 shadow-sm bg-white">
            <CardHeader className="bg-white border-b border-slate-100">
              <CardTitle className="flex items-center gap-2">
                <Mail className="text-indigo-600" />
                Notification Emails
              </CardTitle>
              <CardDescription>Email addresses for automated notifications (Exit Management, Approvals, etc.)</CardDescription>
            </CardHeader>
            <CardContent className="p-6 bg-white">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="hr_email">HR Department Email</Label>
                  <Input
                    id="hr_email"
                    type="email"
                    value={formData.hr_email || ''}
                    onChange={(e) => handleChange('hr_email', e.target.value)}
                    placeholder="hr@company.com"
                    data-testid="hr-email-input"
                  />
                  <p className="text-xs text-slate-500">Receives exit initiation, handover completion notifications</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="finance_email">Finance Department Email</Label>
                  <Input
                    id="finance_email"
                    type="email"
                    value={formData.finance_email || ''}
                    onChange={(e) => handleChange('finance_email', e.target.value)}
                    placeholder="finance@company.com"
                    data-testid="finance-email-input"
                  />
                  <p className="text-xs text-slate-500">Receives F&F settlement notifications when clearance is complete</p>
                </div>
              </div>
            </CardContent>
          </Card>

        {/* SMTP Email Settings — outside the main form */}
        <Card className="border border-orange-200 shadow-sm bg-white" data-testid="smtp-settings-card">
          <CardHeader className="bg-orange-50 border-b border-orange-100">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2 text-orange-800">
                  <Send className="text-orange-600" size={20} />
                  SMTP Email Settings
                </CardTitle>
                <CardDescription>
                  Configure outgoing email server for sending offer letters, notifications, etc.
                  {smtpMeta.source && (
                    <span className={`ml-2 px-2 py-0.5 rounded text-xs font-medium ${smtpMeta.source === 'database' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'}`}>
                      Source: {smtpMeta.source === 'database' ? 'Database (no restart needed)' : 'Environment file'}
                    </span>
                  )}
                </CardDescription>
              </div>
              {smtpMeta.configured && (
                <span className="flex items-center gap-1 text-xs font-medium text-green-700 bg-green-100 px-2 py-1 rounded-full">
                  <CheckCircle2 size={14} /> Configured
                </span>
              )}
            </div>
          </CardHeader>
          <CardContent className="p-6 bg-white">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="smtp_host">SMTP Host</Label>
                <Input
                  id="smtp_host"
                  value={smtpData.smtp_host}
                  onChange={(e) => handleSmtpChange('smtp_host', e.target.value)}
                  placeholder="smtp.gmail.com"
                  data-testid="smtp-host-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="smtp_port">SMTP Port</Label>
                <Input
                  id="smtp_port"
                  type="number"
                  value={smtpData.smtp_port}
                  onChange={(e) => handleSmtpChange('smtp_port', parseInt(e.target.value) || 587)}
                  placeholder="587"
                  data-testid="smtp-port-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="smtp_email">Email Address *</Label>
                <Input
                  id="smtp_email"
                  type="email"
                  value={smtpData.smtp_email}
                  onChange={(e) => handleSmtpChange('smtp_email', e.target.value)}
                  placeholder="help@tnservices.in"
                  data-testid="smtp-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="smtp_password">
                  App Password *
                  {smtpMeta.password_masked && !smtpPasswordEdited && (
                    <span className="ml-2 text-xs text-slate-400 font-normal">Current: {smtpMeta.password_masked}</span>
                  )}
                </Label>
                <div className="relative">
                  <Input
                    id="smtp_password"
                    type={showSmtpPassword ? 'text' : 'password'}
                    value={smtpData.smtp_password}
                    onChange={(e) => handleSmtpChange('smtp_password', e.target.value)}
                    placeholder={smtpMeta.password_masked ? 'Enter new password to change' : 'Enter Gmail App Password'}
                    className="pr-10"
                    data-testid="smtp-password-input"
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    onClick={() => setShowSmtpPassword(!showSmtpPassword)}
                  >
                    {showSmtpPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                <p className="text-xs text-slate-500">
                  Use a Google App Password (not your regular password). <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer" className="text-blue-600 underline">Generate one here</a>
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="smtp_from_name">From Name</Label>
                <Input
                  id="smtp_from_name"
                  value={smtpData.smtp_from_name}
                  onChange={(e) => handleSmtpChange('smtp_from_name', e.target.value)}
                  placeholder="T&N Business Services"
                  data-testid="smtp-from-name-input"
                />
              </div>
            </div>

            {/* Test Result */}
            {smtpTestResult && (
              <div className={`mt-4 p-4 rounded-lg border ${smtpTestResult.status === 'SUCCESS' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                <div className="flex items-center gap-2 mb-1">
                  {smtpTestResult.status === 'SUCCESS' ? (
                    <CheckCircle2 className="text-green-600" size={18} />
                  ) : (
                    <Mail className="text-red-600" size={18} />
                  )}
                  <span className={`font-medium text-sm ${smtpTestResult.status === 'SUCCESS' ? 'text-green-700' : 'text-red-700'}`}>
                    {smtpTestResult.status === 'SUCCESS' ? 'Connection Successful!' : 'Connection Failed'}
                  </span>
                  <span className="text-xs text-slate-500 ml-auto">Source: {smtpTestResult.source}</span>
                </div>
                {smtpTestResult.error && (
                  <p className="text-sm text-red-600 mt-1">{smtpTestResult.error}</p>
                )}
                {smtpTestResult.status === 'SUCCESS' && (
                  <p className="text-sm text-green-600 mt-1">Connected as {smtpTestResult.smtp_email}</p>
                )}
              </div>
            )}

            <div className="flex gap-3 mt-6">
              <Button
                type="button"
                onClick={handleSmtpSave}
                disabled={smtpSaving || (!smtpPasswordEdited && !smtpData.smtp_password)}
                className="bg-orange-600 hover:bg-orange-700 text-white"
                data-testid="smtp-save-btn"
              >
                {smtpSaving ? <Loader2 className="mr-2 animate-spin" size={16} /> : <Save className="mr-2" size={16} />}
                {smtpSaving ? 'Saving...' : 'Save SMTP Settings'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={handleSmtpTest}
                disabled={smtpTesting}
                className="border-orange-300 text-orange-700 hover:bg-orange-50"
                data-testid="smtp-test-btn"
              >
                {smtpTesting ? <Loader2 className="mr-2 animate-spin" size={16} /> : <Send className="mr-2" size={16} />}
                {smtpTesting ? 'Testing...' : 'Test Connection'}
              </Button>
            </div>
          </CardContent>
        </Card>


          {/* Bank Information */}
          <Card className="border border-slate-100 shadow-sm bg-white">
            <CardHeader className="bg-white border-b border-slate-100">
              <CardTitle className="flex items-center gap-2">
                <Globe className="text-indigo-600" />
                Bank Information
              </CardTitle>
              <CardDescription>Banking details (optional)</CardDescription>
            </CardHeader>
            <CardContent className="p-6 bg-white">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="bank_name">Bank Name</Label>
                  <Input
                    id="bank_name"
                    value={formData.bank_name}
                    onChange={(e) => handleChange('bank_name', e.target.value)}
                    placeholder="HDFC Bank"
                    data-testid="bank-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="bank_account">Account Number</Label>
                  <Input
                    id="bank_account"
                    value={formData.bank_account}
                    onChange={(e) => handleChange('bank_account', e.target.value)}
                    placeholder="1234567890"
                    data-testid="bank-account-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ifsc_code">IFSC Code</Label>
                  <Input
                    id="ifsc_code"
                    value={formData.ifsc_code}
                    onChange={(e) => handleChange('ifsc_code', e.target.value)}
                    placeholder="HDFC0001234"
                    maxLength={11}
                    data-testid="ifsc-input"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Save Button */}
          <div className="flex justify-end">
            <Button
              type="submit"
              className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-full px-8 py-6 font-semibold"
              disabled={saving}
              data-testid="save-settings-btn"
            >
              <Save className="mr-2" size={20} />
              {saving ? 'Saving...' : 'Save Company Settings'}
            </Button>
          </div>
        </form>

        {/* Production Data Sync Section - Admin Only */}
        {user?.role === 'admin' && (
          <Card className="border border-amber-200 shadow-sm bg-amber-50">
            <CardHeader className="bg-amber-50 border-b border-amber-200">
              <CardTitle className="flex items-center gap-2">
                <Database className="text-amber-600" />
                Production Data Sync
              </CardTitle>
              <CardDescription>
                Synchronize essential data after deployment. This will reset admin password, 
                seed departments/designations, and sync company settings.
              </CardDescription>
            </CardHeader>
            <CardContent className="p-6 bg-amber-50">
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <Button
                    type="button"
                    variant="outline"
                    className="border-amber-400 text-amber-700 hover:bg-amber-100"
                    onClick={handleSyncData}
                    disabled={syncing}
                    data-testid="sync-data-btn"
                  >
                    <RefreshCw className={`mr-2 ${syncing ? 'animate-spin' : ''}`} size={18} />
                    {syncing ? 'Syncing...' : 'Sync All Data'}
                  </Button>
                  <p className="text-sm text-amber-700">
                    Use this after deploying to production to ensure all essential data is set up.
                  </p>
                </div>

                {/* Sync Results */}
                {syncResult && !syncResult.error && (
                  <div className="p-4 bg-white rounded-lg border border-green-200">
                    <h4 className="font-medium text-green-700 flex items-center gap-2 mb-3">
                      <CheckCircle2 size={18} />
                      Sync Completed Successfully
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <p className="text-slate-500">Admin Reset</p>
                        <p className="font-medium text-slate-900">{syncResult.admin_reset || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-slate-500">Departments Created</p>
                        <p className="font-medium text-slate-900">
                          {syncResult.departments_seeding?.created_departments || 0}
                        </p>
                      </div>
                      <div>
                        <p className="text-slate-500">Designations Created</p>
                        <p className="font-medium text-slate-900">
                          {syncResult.departments_seeding?.created_designations || 0}
                        </p>
                      </div>
                      <div>
                        <p className="text-slate-500">Company Settings</p>
                        <p className="font-medium text-slate-900">{syncResult.company_settings || 'N/A'}</p>
                      </div>
                    </div>
                    {syncResult.final_counts && (
                      <div className="mt-4 pt-4 border-t border-slate-200">
                        <p className="text-sm text-slate-500 mb-2">Final Database Counts:</p>
                        <div className="flex flex-wrap gap-4 text-sm">
                          <span className="px-2 py-1 bg-slate-100 rounded">
                            Departments: {syncResult.final_counts.departments}
                          </span>
                          <span className="px-2 py-1 bg-slate-100 rounded">
                            Designations: {syncResult.final_counts.designations}
                          </span>
                          <span className="px-2 py-1 bg-slate-100 rounded">
                            Users: {syncResult.final_counts.users}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {syncResult?.error && (
                  <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                    <p className="text-red-700">Error: {syncResult.error}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
};

export default CompanySettings;
